import React, { useState } from 'react';
import { Popover } from 'antd';
import { Button, DatePicker } from 'Components/nui';
import { FormattedMessage } from 'react-intl';
import Icon from 'Components/Icon';
import dayjs from 'dayjs';
import Tooltip from 'Components/nui/Tooltip';
import classNames from 'classnames';

interface PopoverContentProps {
  handleVisibleChange: (visible: boolean) => void;
  scheduledTime: string | null;
  setScheduledTime: (time: string | null) => void;
  error?: string[];
}

const PopoverContent = ({
  handleVisibleChange,
  scheduledTime,
  setScheduledTime,
  error,
}: PopoverContentProps) => {
  const [selectedTime, setSelectedTime] = useState<Date | string | null>(
    scheduledTime
  );

  const handleSchedule = () => {
    handleVisibleChange(false);
    setScheduledTime(selectedTime ? dayjs(selectedTime).toISOString() : null);
  };

  return (
    <div className="schedule-picker">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div
          className={classNames({
            'has-errors': error && error.length > 0,
          })}
        >
          <DatePicker
            selectsRange={false}
            showTimeSelect
            timeIntervals={15}
            futureTimeOnly
            minDate={new Date()}
            onChange={(date: Date | null) => {
              setSelectedTime(date);
              if (date === null) {
                setScheduledTime(null);
                handleVisibleChange(false);
              }
            }}
            initialValue={scheduledTime}
            size="large"
            monthsShown={1}
            dateFormat="d MMM yyyy HH:mm"
          />
        </div>
        <Button
          htmlType="button"
          onClick={handleSchedule}
          disabled={!selectedTime}
        >
          <FormattedMessage
            id="orderform-schedule-button-confirm"
            defaultMessage="Save"
          />
        </Button>
      </div>
      {error && error.length > 0 && (
        <ul className="error-list mt-10">
          {error.map((e: string, i: number) => (
            <li key={i} className="error-description">
              {e}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

interface ScheduleButtonProps {
  scheduledTime: string | null;
  setScheduledTime: (time: string | null) => void;
  onVisibleChange?: (visible: boolean) => void;
  disabled?: boolean;
  visible?: boolean;
  setVisible?: (visible: boolean) => void;
  error?: string[];
}

export function ScheduleButton({
  scheduledTime,
  setScheduledTime,
  onVisibleChange,
  disabled,
  visible: externalVisible,
  setVisible: externalSetVisible,
  error,
}: ScheduleButtonProps) {
  const [internalVisible, setInternalVisible] = useState(false);

  const visible =
    externalVisible !== undefined ? externalVisible : internalVisible;
  const setVisible = externalSetVisible || setInternalVisible;

  const handleVisibleChange = (v: boolean) => {
    setVisible(v);
    onVisibleChange?.(v);
  };

  return (
    <Popover
      content={
        <PopoverContent
          scheduledTime={scheduledTime}
          setScheduledTime={setScheduledTime}
          handleVisibleChange={handleVisibleChange}
          error={error}
        />
      }
      title={
        <FormattedMessage
          id="orderform-schedule-modal-title"
          defaultMessage="Schedule for later"
        />
      }
      trigger="click"
      visible={visible}
      onVisibleChange={handleVisibleChange}
    >
      <Tooltip
        title={
          <FormattedMessage
            id="orderform-button-calendar-tooltip"
            defaultMessage="Schedule for later"
          />
        }
      >
        <Button
          htmlType="button"
          className={scheduledTime ? 'scheduled' : ''}
          disabled={disabled}
        >
          <Icon name="calendar_today" />
        </Button>
      </Tooltip>
    </Popover>
  );
}
